import { Outlet, useLocation } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Briefcase, ClipboardCheck, Database, Shield, FolderOpen, X,
  type LucideIcon,
} from 'lucide-react'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import { ContextFeatureBar } from './ContextFeatureBar'
import { GlobalShortcuts } from './GlobalShortcuts'
import { ProjectStackBar, type ProjectStackStep } from './ProjectStackBar'
import { RightPanel } from './RightPanel'
import { UpdateBanner } from './UpdateBanner'
import { AppFooter } from '@/components/shared/PageShell'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { GrcCopilotSidebar } from '@/components/copilot/GrcCopilotSidebar'
import { useClientContext } from '@/stores/client-context.store'
import { useContextStore } from '@/stores/context-store'
import { getRightPanelCardsForContext } from '@/config/right-panel'
import { tn } from '@/i18n/navLabels'
import type { GrcContextId } from '@/config/contexts'

interface ContextProjectShape {
  type: string
  icon: LucideIcon
  accent: 'blue' | 'amber' | 'green' | 'purple' | 'red' | 'teal' | 'indigo' | 'slate'
  steps: ProjectStackStep[]
}

const PROJECT_SHAPE_BY_CONTEXT: Partial<Record<GrcContextId, ContextProjectShape>> = {
  auditeur: {
    type: 'Mission audit',
    icon: ClipboardCheck,
    accent: 'teal',
    steps: [
      { id: 'plan', label: 'Planification', done: true },
      { id: 'exec', label: 'Exécution', done: true },
      { id: 'find', label: 'Constats', done: false },
      { id: 'foll', label: 'Suivi', done: false },
      { id: 'cert', label: 'Certification', done: false },
    ],
  },
  'consultant-grc': {
    type: 'Mission GRC',
    icon: Briefcase,
    accent: 'blue',
    steps: [
      { id: 'scope', label: 'Cadrage', done: true },
      { id: 'diag', label: 'Diagnostic', done: false },
      { id: 'plan', label: 'Plan', done: false },
      { id: 'exec', label: 'Mise en œuvre', done: false },
      { id: 'rev', label: 'Revue', done: false },
    ],
  },
  'expert-securite': {
    type: 'Engagement sécurité',
    icon: Shield,
    accent: 'red',
    steps: [
      { id: 'scope', label: 'Cadrage', done: true },
      { id: 'pent', label: 'Tests', done: false },
      { id: 'find', label: 'Constats', done: false },
      { id: 'rem', label: 'Remédiation', done: false },
    ],
  },
  'expert-donnees-ia': {
    type: 'Programme Data/IA',
    icon: Database,
    accent: 'purple',
    steps: [
      { id: 'inv', label: 'Inventaire', done: true },
      { id: 'gap', label: 'Gaps', done: false },
      { id: 'plan', label: 'Plan', done: false },
      { id: 'exec', label: 'Mise en œuvre', done: false },
    ],
  },
  'directeur-mission': {
    type: 'Mandat',
    icon: Briefcase,
    accent: 'blue',
    steps: [
      { id: 'open', label: 'Ouverture', done: true },
      { id: 'plan', label: 'Planification', done: false },
      { id: 'exec', label: 'Exécution', done: false },
      { id: 'qa', label: 'Qualité', done: false },
      { id: 'close', label: 'Clôture', done: false },
    ],
  },
  analyste: {
    type: 'Campagne',
    icon: ClipboardCheck,
    accent: 'indigo',
    steps: [
      { id: 'scope', label: 'Cadrage', done: true },
      { id: 'collect', label: 'Collecte', done: false },
      { id: 'analyze', label: 'Analyse', done: false },
      { id: 'report', label: 'Rapport', done: false },
    ],
  },
  formateur: {
    type: 'Programme',
    icon: Briefcase,
    accent: 'green',
    steps: [
      { id: 'design', label: 'Design', done: true },
      { id: 'pilot', label: 'Pilote', done: false },
      { id: 'rollout', label: 'Déploiement', done: false },
      { id: 'measure', label: 'Mesure', done: false },
    ],
  },
}

const FALLBACK_SHAPE: ContextProjectShape = {
  type: 'Dossier',
  icon: FolderOpen,
  accent: 'blue',
  steps: [],
}

export function MainLayout() {
  const { t } = useTranslation()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [rightCollapsed, setRightCollapsed] = useState(false)
  // #ADMINCLIENT-mobile-hamburger: on phones the sidebar collapses to a drawer.
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const location = useLocation()

  // Close the mobile drawer whenever the route changes (a nav link was tapped).
  useEffect(() => {
    setMobileNavOpen(false)
  }, [location.pathname])

  // Escape closes the mobile drawer + lock body scroll while it is open.
  useEffect(() => {
    if (!mobileNavOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileNavOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mobileNavOpen])
  const { activeContext } = useContextStore()
  const {
    activeClientId,
    activeClientName,
    activeDossierName,
    clearContext,
  } = useClientContext()

  const rawShape = PROJECT_SHAPE_BY_CONTEXT[activeContext] ?? FALLBACK_SHAPE
  // Localize the (French-authored) project shape type + step labels.
  const projectShape = {
    ...rawShape,
    type: tn(rawShape.type),
    steps: rawShape.steps.map((s) => ({ ...s, label: tn(s.label) })),
  }
  const projectTitle =
    activeDossierName ||
    activeClientName ||
    (activeClientId ? `Client ${activeClientId.slice(0, 8)}` : null)

  // Guard against an unknown / invalid active context: a bad context id must not
  // throw out of the shell (white-screen). Fall back to an empty right-rail.
  let rightCards: ReturnType<typeof getRightPanelCardsForContext> = []
  try {
    rightCards = getRightPanelCardsForContext(activeContext)
  } catch {
    rightCards = []
  }

  // Copilot context — active cabinet role + current route.
  const { moduleContext, pageContext } = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    return {
      moduleContext: segments[0] || activeContext || 'dashboard',
      pageContext: segments[segments.length - 1] || 'overview',
    }
  }, [location.pathname, activeContext])

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* WCAG 2.4.1 Bypass Blocks — skip-to-content link, first focusable element. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring"
      >
        {t('shell.skipToContent')}
      </a>
      {/* App-wide keyboard shortcuts (Ctrl/⌘K, /, n, g-sequences) + ? help overlay */}
      <GlobalShortcuts />
      {/* Desktop sidebar — hidden below lg (1024px), where a hamburger drawer
          takes over. Between md/lg the 260px rail + 320px right panel crushed
          <main>, so nav lives in the drawer on phones AND tablets. */}
      <div className="hidden lg:flex">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      </div>

      {/* #ADMINCLIENT-mobile-hamburger: off-canvas slide-in drawer for phones +
          tablets (< lg). Mirrors the client portal's mobile nav pattern. */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-50 flex lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Menu de navigation"
        >
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
            aria-hidden
            onClick={() => setMobileNavOpen(false)}
          />
          <div className="relative z-10 h-full shadow-2xl animate-in slide-in-from-left duration-300 ease-out">
            <button
              type="button"
              onClick={() => setMobileNavOpen(false)}
              aria-label="Fermer le menu"
              className="absolute right-2 top-3 z-20 flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 text-[hsl(var(--sidebar-fg))] backdrop-blur-sm transition-colors hover:bg-white/20"
            >
              <X className="h-4 w-4" />
            </button>
            <Sidebar collapsed={false} onToggle={() => setMobileNavOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
        <UpdateBanner />
        <TopBar onMenuClick={() => setMobileNavOpen(true)} />

        {/* Context feature bar — key features of the active cabinet context
            (shortcut layer, just below the context selector). Per-context accent;
            aligned with the mobile FeatureBar / PRO_PERSONA_TABS feature sets. */}
        <ContextFeatureBar />

        {projectTitle && (
          <ProjectStackBar
            title={projectTitle}
            type={projectShape.type}
            icon={projectShape.icon}
            accent={projectShape.accent}
            steps={projectShape.steps.length > 0 ? projectShape.steps : undefined}
            onClose={clearContext}
          />
        )}

        <div className="flex flex-1 min-h-0 overflow-hidden">
          <main
            id="main-content"
            tabIndex={-1}
            className="flex-1 min-w-0 min-h-0 overflow-hidden bg-muted/30 focus:outline-none"
          >
            <div className="h-full overflow-auto p-6 scrollbar-thin">
              <ErrorBoundary key={location.pathname}>
                <Outlet />
              </ErrorBoundary>
            </div>
          </main>

          {/* #ADMINCLIENT-mobile-hamburger: like the 260px left rail, the 320px
              right panel is hidden below lg so it can't squeeze the main content
              on phones/tablets (main was left ~55px at 375px otherwise). */}
          <div className="hidden lg:flex min-h-0">
            <RightPanel
              cards={rightCards}
              collapsed={rightCollapsed}
              onToggle={() => setRightCollapsed((c) => !c)}
            />
          </div>
        </div>

        {/* App-wide footer band — consistent brand/quick-links/back-to-top on
            every route (there was no app-level footer before). */}
        <AppFooter />
      </div>

      {/* Floating AI copilot — always available on every route/context. */}
      <GrcCopilotSidebar moduleContext={moduleContext} pageContext={pageContext} />
    </div>
  )
}
